/**
 * ChatPanel.mjs
 *
 * Perplexity-style bottom-anchored chat panel.
 * Starts as a compact input bar + persona chips at the bottom of the article,
 * grows upward when messages are sent (up to 600px desktop / 500px mobile).
 * Glassmorphic, theme-aware. Reuses conversationSlice Redux plumbing.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ChatMessageContent from './ChatMessageContent.mjs';
import {
    clearError,
    fetchAvailablePersonas,
    resetConversation,
    selectIsConversationExpanded,
    sendMessageStreaming,
    setConversationExpanded,
    setSelectedPersona,
    resumeConversation,
    toggleConversationExpanded,
    updateUserInput,
} from '../../slices/conversationSlice.mjs';
import useIsMounted from '../../hooks/useIsMounted.mjs';

/* ─── HELPER: auto-grow textarea ─── */
const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
};

/* ─── SEND ICON ─── */
const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

/* ─── STARTER QUESTIONS ─── */
const StarterQuestions = ({ starters, onSelect, disabled }) => {
    if (!starters?.length) return null;
    return (
        <div className="chat-panel__starters">
            {starters.map((question, idx) => (
                <button
                    key={idx}
                    className="chat-panel__starter-pill"
                    onClick={() => onSelect(question, idx)}
                    disabled={disabled}
                >
                    <span className="chat-panel__starter-icon">💬</span>
                    {question}
                </button>
            ))}
        </div>
    );
};

/* ─── MAIN COMPONENT ─── */
const ChatPanel = ({ user, articleId, article }) => {
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const isExpanded = useSelector(selectIsConversationExpanded);

    const {
        messages,
        currentUserInput,
        isLoading,
        error,
        currentInternalConversationId,
        availablePersonas,
        selectedPersona,
        isLoadingPersonas,
        personasError,
    } = useSelector((state) => state.conversationSlice);

    // Persona matching: match article's author persona to available personas
    const currentArticle = useSelector(
        (state) => state.unifiedArticles?.currentArticle || state.siteArticles?.currentArticle
    );
    const matchingPersonaForArticle = useMemo(() => {
        if (!currentArticle?.authorPersona?.name || !Array.isArray(availablePersonas) || availablePersonas.length === 0) {
            return null;
        }
        return availablePersonas.find((p) => p.name === currentArticle.authorPersona.name) || null;
    }, [currentArticle, availablePersonas]);
    const firstAvailablePersona = availablePersonas.length > 0 ? availablePersonas[0] : null;

    const [userHasManuallySelected, setUserHasManuallySelected] = useState(false);
    const [justSentMessage, setJustSentMessage] = useState(false);
    const [resumeSettled, setResumeSettled] = useState(false);
    const messagesContainerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const panelRef = useRef(null);
    const userHasManuallyClosed = useRef(false);
    const conversationFetchAttemptedForPersonaRef = useRef(null);

    /* ─── MEASURE HEADER → set --header-height ─── */
    useEffect(() => {
        if (!isMounted || !panelRef.current) return;
        const header = document.querySelector('.app-header');
        if (!header) return;

        const update = () => {
            const h = header.offsetHeight;
            panelRef.current?.style.setProperty('--header-height', `${h}px`);
        };

        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [isMounted, isExpanded]);

    /* ─── PERSONA FETCH + AUTO-SELECT + RESUME ─── */
    useEffect(() => {
        if (!isMounted || !user) return;

        // 1. Fetch personas
        if ((!Array.isArray(availablePersonas) || availablePersonas.length === 0) && !isLoadingPersonas && !personasError) {
            dispatch(fetchAvailablePersonas());
        }

        // 2. Auto-select
        if (availablePersonas.length > 0 && !userHasManuallySelected) {
            const personaToSelect = matchingPersonaForArticle || selectedPersona || firstAvailablePersona;
            if (personaToSelect && (!selectedPersona || selectedPersona._id !== personaToSelect._id)) {
                dispatch(setSelectedPersona(personaToSelect));
            }
        }

        // 3. Resume conversation
        if (articleId && selectedPersona?._id) {
            if (conversationFetchAttemptedForPersonaRef.current !== selectedPersona._id) {
                conversationFetchAttemptedForPersonaRef.current = null;
                setResumeSettled(false);
            }
            if (!currentInternalConversationId && !isLoading && !error && !conversationFetchAttemptedForPersonaRef.current) {
                conversationFetchAttemptedForPersonaRef.current = selectedPersona._id;
                dispatch(resumeConversation({ articleId, personaId: selectedPersona._id }))
                    .unwrap()
                    .then(() => setResumeSettled(true))
                    .catch(() => {
                        conversationFetchAttemptedForPersonaRef.current = null;
                        setResumeSettled(true);
                    });
            }
        }
    }, [
        isMounted, user, articleId,
        availablePersonas, isLoadingPersonas, personasError,
        selectedPersona, currentInternalConversationId,
        isLoading, error,
        matchingPersonaForArticle, firstAvailablePersona,
        userHasManuallySelected, dispatch,
    ]);

    /* Expansion is user-driven:
       - ?chat=open deep-link (SportsArticleDetailPage)
       - textarea focus (existing conversation)
       - send button (new conversation) */

    /* ─── AUTO-SCROLL ─── */
    useEffect(() => {
        if (justSentMessage && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            setJustSentMessage(false);
        }
    }, [justSentMessage]);

    // Scroll on loaded conversation
    useEffect(() => {
        if (currentInternalConversationId && messages.length > 1 && messagesContainerRef.current) {
            const hasStreaming = messages.some((m) => m.isStreaming);
            if (!hasStreaming) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
        }
    }, [currentInternalConversationId, messages.length]);

    // Scroll during streaming
    useEffect(() => {
        const hasStreaming = messages.some((m) => m.isStreaming);
        if (!hasStreaming || !messagesContainerRef.current) return;
        const container = messagesContainerRef.current;
        // Keep near bottom during streaming
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 80;
        if (isNearBottom) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages]);

    /* ─── CLEANUP on unmount ─── */
    useEffect(() => {
        return () => {
            dispatch(resetConversation());
        };
    }, [dispatch]);

    /* ─── HANDLERS ─── */
    const handleSend = useCallback(() => {
        if (!user || !currentUserInput.trim() || isLoading) return;
        if (!selectedPersona?._id) return;

        setJustSentMessage(true);

        // Auto-expand if not already
        if (!isExpanded) {
            dispatch(setConversationExpanded(true));
        }

        dispatch(sendMessageStreaming({
            userMessage: currentUserInput,
            articleId: currentInternalConversationId ? undefined : articleId,
            personaId: currentInternalConversationId ? undefined : selectedPersona._id,
        }));
    }, [user, currentUserInput, isLoading, selectedPersona, currentInternalConversationId, articleId, isExpanded, dispatch]);

    const handleStarterSelect = useCallback((question, idx) => {
        if (!user || isLoading || !selectedPersona?._id) return;

        setJustSentMessage(true);

        // Auto-expand
        if (!isExpanded) {
            dispatch(setConversationExpanded(true));
        }

        // Set the question as user input and send
        dispatch(updateUserInput(question));
        dispatch(sendMessageStreaming({
            userMessage: question,
            articleId: currentInternalConversationId ? undefined : articleId,
            personaId: currentInternalConversationId ? undefined : selectedPersona._id,
            source: 'starter_question',
            starterIndex: idx,
        }));
    }, [user, isLoading, selectedPersona, currentInternalConversationId, articleId, isExpanded, dispatch]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    const handleInput = useCallback((e) => {
        dispatch(updateUserInput(e.target.value));
        autoResize(e.target);
    }, [dispatch]);

    const handleTextareaFocus = useCallback(() => {
        // Existing conversation → expand immediately on focus
        if ((messages.length > 0 || currentInternalConversationId) && !isExpanded) {
            userHasManuallyClosed.current = false;
            dispatch(setConversationExpanded(true));
        }
    }, [messages.length, currentInternalConversationId, isExpanded, dispatch]);

    const handlePersonaClick = useCallback((persona) => {
        setUserHasManuallySelected(true);
        dispatch(setSelectedPersona(persona));
    }, [dispatch]);

    const handleClose = useCallback(() => {
        userHasManuallyClosed.current = true;
        dispatch(setConversationExpanded(false));
    }, [dispatch]);

    /* ─── CLICK-OUTSIDE → collapse (desktop only) ─── */
    useEffect(() => {
        if (!isExpanded) return;

        const mq = window.matchMedia('(min-width: 1024px)');
        if (!mq.matches) return; // full-screen on mobile → skip

        const onClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                handleClose();
            }
        };

        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [isExpanded, handleClose]);

    /* ─── VISUAL VIEWPORT TRACKING (iOS keyboard handling) ─── */
    useEffect(() => {
        if (!isExpanded || typeof window === 'undefined') return;
        const vv = window.visualViewport;
        if (!vv) return;

        const onViewportChange = () => {
            if (!panelRef.current) return;
            // offsetTop = how far iOS scrolled the visual viewport (keyboard push)
            // height = actual visible area (shrinks when keyboard is up)
            panelRef.current.style.setProperty('--vv-offset', `${vv.offsetTop}px`);
            panelRef.current.style.setProperty('--vv-height', `${vv.height}px`);
        };

        vv.addEventListener('resize', onViewportChange);
        vv.addEventListener('scroll', onViewportChange);
        onViewportChange(); // run once on mount

        return () => {
            vv.removeEventListener('resize', onViewportChange);
            vv.removeEventListener('scroll', onViewportChange);
            if (panelRef.current) {
                panelRef.current.style.removeProperty('--vv-offset');
                panelRef.current.style.removeProperty('--vv-height');
            }
        };
    }, [isExpanded]);

    /* ─── ARTICLE THUMBNAIL ─── */
    const articleThumb = useMemo(() => {
        // Prefer generatedImages thumbUrl from Redux currentArticle
        const genThumb = currentArticle?.generatedImages?.[0]?.thumbUrl;
        if (genThumb) return genThumb;
        // Fallback chain
        if (!article) return null;
        return article.generatedImages?.[0]?.thumbUrl
            || article.images?.[0]?.url
            || article.imageUrl
            || article.heroImage
            || null;
    }, [currentArticle, article]);

    /* ─── RENDER (client-only) ─── */
    if (!isMounted || !user) return null;

    const hasInput = currentUserInput.trim().length > 0;

    return (
        <div ref={panelRef} className={`chat-panel ${isExpanded ? 'chat-panel--expanded' : ''}`} id="section-chat">
            {/* Article preview card (only when expanded) — entire card collapses chat */}
            {isExpanded && article && (
                <div className="chat-panel__article-card" onClick={handleClose} role="button" tabIndex={0} title="Collapse chat">
                    {articleThumb && (
                        <img
                            className="chat-panel__article-thumb"
                            src={articleThumb}
                            alt=""
                            loading="lazy"
                        />
                    )}
                    <span className="chat-panel__article-title">{article.title}</span>
                    <button
                        className="chat-panel__article-dismiss"
                        onClick={(e) => { e.stopPropagation(); handleClose(); }}
                        title="Collapse"
                        aria-label="Collapse chat"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Persona chips */}
            {availablePersonas.length > 0 && (
                <div className="chat-panel__personas">
                    {availablePersonas.map((persona) => (
                        <button
                            key={persona._id}
                            className={`chat-panel__persona-chip ${selectedPersona?._id === persona._id ? 'chat-panel__persona-chip--active' : ''
                                } ${isLoadingPersonas ? 'chat-panel__persona-chip--loading' : ''}`}
                            onClick={() => handlePersonaClick(persona)}
                            disabled={isLoadingPersonas}
                        >
                            {persona.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Messages area (only when expanded) */}
            {isExpanded && (
                <div className="chat-panel__messages" ref={messagesContainerRef}>
                    {messages.length === 0 && !isLoading && (
                        <div className="chat-panel__empty">
                            Ask {selectedPersona?.name || 'the AI'} anything about this article…
                        </div>
                    )}

                    {/* Starter questions — show only when no messages yet */}
                    {messages.length === 0 && !isLoading && article?.conversationStarters?.length > 0 && (
                        <StarterQuestions
                            starters={article.conversationStarters}
                            onSelect={handleStarterSelect}
                            disabled={!selectedPersona || isLoading}
                        />
                    )}

                    {messages.map((msg, idx) => (
                        <div
                            key={msg._id || `msg-${idx}`}
                            className={`chat-panel__msg chat-panel__msg--${msg.role} ${msg.isStreaming ? 'chat-panel__msg--streaming' : ''
                                }`}
                        >
                            <ChatMessageContent content={msg.content} />
                            {msg.isStreaming && <span className="chat-panel__cursor">▊</span>}
                        </div>
                    ))}

                    <div ref={messagesEndRef} />
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="chat-panel__error">
                    {error}
                    <button onClick={() => dispatch(clearError())}>Dismiss</button>
                </div>
            )}

            {/* Input area */}
            <div className="chat-panel__input-area">
                <textarea
                    ref={textareaRef}
                    className="chat-panel__textarea"
                    value={currentUserInput}
                    onChange={handleInput}
                    onKeyDown={handleKeyDown}
                    onFocus={handleTextareaFocus}
                    placeholder={
                        (!resumeSettled || isLoading) ? ''
                            : selectedPersona
                                ? (messages.length > 0 || currentInternalConversationId)
                                    ? 'Continue your conversation…'
                                    : `Ask ${selectedPersona.name} a question…`
                                : ''
                    }
                    rows={1}
                    disabled={!resumeSettled || isLoading || !selectedPersona}
                />
                <button
                    className={`chat-panel__send-btn ${hasInput && !isLoading ? 'chat-panel__send-btn--visible' : ''}`}
                    onClick={handleSend}
                    disabled={!hasInput || isLoading || !selectedPersona}
                    aria-label="Send message"
                    title="Send"
                >
                    <SendIcon />
                </button>
            </div>
        </div>
    );
};

export default ChatPanel;
